import * as React from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  addYears,
  subYears,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  getMonth,
  getYear,
  setMonth as dfSetMonth,
  setYear as dfSetYear,
  endOfMonth as dfEndOfMonth,
  startOfMonth as dfStartOfMonth,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AnchoredPopover } from '@/components/ui/anchored-popover';

// ──────────────────────────────────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────────────────────────────────

export interface GlassDateRangePickerProps {
  /** ISO YYYY-MM-DD ou string vazia */
  from: string;
  /** ISO YYYY-MM-DD ou string vazia */
  to: string;
  onChange: (range: { from: string; to: string }) => void;
  className?: string;
  /** Label do trigger quando vazio. Default: "Período" */
  placeholder?: string;
  'aria-label'?: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// Helpers internos
// ──────────────────────────────────────────────────────────────────────────────

type View = 'days' | 'months' | 'years';

const WEEK_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MONTH_LABELS = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

function calcYgStart(year: number): number {
  return Math.floor((year - 1) / 12) * 12 + 1;
}

function buildCalendarDays(month: Date): Date[] {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
  const end = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
  const days: Date[] = [];
  let cur = start;
  while (cur <= end) {
    days.push(cur);
    cur = addDays(cur, 1);
  }
  return days;
}

function toIso(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function parseIsoValue(v: string): Date | null {
  if (!v) return null;
  try {
    const d = parseISO(v);
    if (isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

function fmtDisplay(iso: string): string {
  const d = parseIsoValue(iso);
  return d ? format(d, 'dd/MM/yyyy') : '';
}

/** Parseia "DD/MM/YYYY" digitado → YYYY-MM-DD. Retorna '' se inválido. */
function parseBrDate(br: string): string {
  const clean = br.replace(/\D/g, '');
  if (clean.length !== 8) return '';
  const day = Number(clean.slice(0, 2));
  const month = Number(clean.slice(2, 4));
  const year = Number(clean.slice(4, 8));
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return '';
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return '';
  return toIso(d);
}

/** Máscara "DD/MM/AAAA" enquanto o usuário digita */
function applyDateMask(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) out += '/';
    out += digits[i];
  }
  return out;
}

// Classes compartilhadas entre células de meses e anos
const CELL_BASE =
  'flex items-center justify-center rounded-md text-xs font-medium transition cursor-pointer select-none h-9';
const CELL_DEFAULT =
  'text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]';
const CELL_SELECTED = 'bg-[var(--accent)] text-white hover:opacity-90';
const CELL_TODAY = 'ring-1 ring-[var(--accent)] text-[var(--accent)]';

// ──────────────────────────────────────────────────────────────────────────────
// Sub-componente: mini-calendário para um lado do range
// ──────────────────────────────────────────────────────────────────────────────

interface MiniCalendarProps {
  month: Date;
  onMonthChange: (m: Date) => void;
  fromDate: Date | null;
  toDate: Date | null;
  onSelect: (iso: string) => void;
  view: View;
  onViewChange: (v: View) => void;
  ygStart: number;
  onYgStartChange: (s: number) => void;
}

function MiniCalendar({
  month,
  onMonthChange,
  fromDate,
  toDate,
  onSelect,
  view,
  onViewChange,
  ygStart,
  onYgStartChange,
}: MiniCalendarProps) {
  const days = buildCalendarDays(month);
  const currentYear = getYear(month);
  const yearGroup = Array.from({ length: 12 }, (_, i) => ygStart + i);
  const nowYear = getYear(new Date());
  const nowMonth = getMonth(new Date());

  function isInRange(day: Date): boolean {
    if (!fromDate || !toDate) return false;
    const iso = toIso(day);
    return iso > toIso(fromDate) && iso < toIso(toDate);
  }

  function handlePrev() {
    if (view === 'days') onMonthChange(subMonths(month, 1));
    else if (view === 'months') onMonthChange(subYears(month, 1));
    else onYgStartChange(ygStart - 12);
  }

  function handleNext() {
    if (view === 'days') onMonthChange(addMonths(month, 1));
    else if (view === 'months') onMonthChange(addYears(month, 1));
    else onYgStartChange(ygStart + 12);
  }

  function selectMonth(monthIndex: number) {
    onMonthChange(dfSetMonth(month, monthIndex));
    onViewChange('days');
  }

  function selectYear(year: number) {
    onMonthChange(dfSetYear(month, year));
    onYgStartChange(calcYgStart(year));
    onViewChange('months');
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-[var(--popover-border,var(--card-border))]">
        <button
          type="button"
          onClick={handlePrev}
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
          aria-label="Anterior"
        >
          <ChevronLeft className="h-3.5 w-3.5 text-[var(--fg)]" />
        </button>

        {view === 'days' && (
          <div className="flex items-center gap-0.5 text-xs font-semibold text-[var(--fg)]">
            <button
              type="button"
              onClick={() => onViewChange('months')}
              className="rounded px-1 py-0.5 hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition capitalize"
            >
              {format(month, 'MMMM', { locale: ptBR })}
            </button>
            <button
              type="button"
              onClick={() => onViewChange('years')}
              className="rounded px-1 py-0.5 hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
            >
              {currentYear}
            </button>
          </div>
        )}

        {view === 'months' && (
          <button
            type="button"
            onClick={() => onViewChange('years')}
            className="text-xs font-semibold text-[var(--fg)] rounded px-1 py-0.5 hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
          >
            {currentYear}
          </button>
        )}

        {view === 'years' && (
          <span className="text-xs font-semibold text-[var(--fg)] select-none">
            {ygStart} – {ygStart + 11}
          </span>
        )}

        <button
          type="button"
          onClick={handleNext}
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
          aria-label="Próximo"
        >
          <ChevronRight className="h-3.5 w-3.5 text-[var(--fg)]" />
        </button>
      </div>

      {/* View: dias */}
      {view === 'days' && (
        <>
          <div className="grid grid-cols-7 px-1.5 pt-1.5">
            {WEEK_LABELS.map((l, i) => (
              <div
                key={i}
                className="flex h-7 items-center justify-center text-[10px] font-medium text-[var(--fg-muted)]"
              >
                {l}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 px-1.5 pb-1.5">
            {days.map((day, idx) => {
              const iso = toIso(day);
              const isCurrentMonth = isSameMonth(day, month);
              const isFromDay = fromDate ? isSameDay(day, fromDate) : false;
              const isToDay = toDate ? isSameDay(day, toDate) : false;
              const isSelectedDay = isFromDay || isToDay;
              const isTodayDay = isToday(day);
              const inRange = isInRange(day);

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => onSelect(iso)}
                  className={cn(
                    'flex h-7 w-full items-center justify-center rounded-md text-xs transition',
                    !isCurrentMonth && 'text-[var(--fg-muted)] opacity-40',
                    isCurrentMonth && !isSelectedDay && !isTodayDay && !inRange &&
                      'text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]',
                    isTodayDay && !isSelectedDay && !inRange &&
                      'ring-1 ring-[var(--accent)] text-[var(--accent)]',
                    inRange && !isSelectedDay &&
                      'bg-[color-mix(in_oklch,var(--accent)_15%,transparent)] text-[var(--fg)]',
                    isSelectedDay &&
                      'bg-[var(--accent)] text-white font-medium hover:opacity-90',
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* View: meses */}
      {view === 'months' && (
        <div className="grid grid-cols-4 gap-1 px-2 py-2">
          {MONTH_LABELS.map((label, idx) => {
            const isSelectedMonth =
              (!!fromDate && getYear(fromDate) === currentYear && getMonth(fromDate) === idx) ||
              (!!toDate && getYear(toDate) === currentYear && getMonth(toDate) === idx);
            const isTodayMonth = nowYear === currentYear && nowMonth === idx;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => selectMonth(idx)}
                className={cn(
                  CELL_BASE,
                  isSelectedMonth ? CELL_SELECTED : isTodayMonth ? CELL_TODAY : CELL_DEFAULT,
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* View: anos */}
      {view === 'years' && (
        <div className="grid grid-cols-4 gap-1 px-2 py-2">
          {yearGroup.map((yr) => {
            const isSelectedYear =
              (!!fromDate && getYear(fromDate) === yr) || (!!toDate && getYear(toDate) === yr);
            const isTodayYear = nowYear === yr;
            return (
              <button
                key={yr}
                type="button"
                onClick={() => selectYear(yr)}
                className={cn(
                  CELL_BASE,
                  isSelectedYear ? CELL_SELECTED : isTodayYear ? CELL_TODAY : CELL_DEFAULT,
                )}
              >
                {yr}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Componente principal
// ──────────────────────────────────────────────────────────────────────────────

export function GlassDateRangePicker({
  from,
  to,
  onChange,
  className,
  placeholder = 'Período',
  'aria-label': ariaLabel,
}: GlassDateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);

  // Estado INTERNO (pending) — só propaga via onChange quando ambos válidos.
  // Evita request prematura (com só "De" preenchido) e mantém highlight
  // atualizado ao digitar nos inputs antes de fechar o popover.
  const [internalFrom, setInternalFrom] = React.useState<string>(from);
  const [internalTo, setInternalTo] = React.useState<string>(to);

  // Sincroniza interno com prop quando prop muda externamente (limpar, etc)
  React.useEffect(() => { setInternalFrom(from); }, [from]);
  React.useEffect(() => { setInternalTo(to); }, [to]);

  /** Propaga pro parent apenas se: ambos preenchidos válidos, ou ambos vazios. */
  function emitIfReady(nextFrom: string, nextTo: string) {
    const bothEmpty = !nextFrom && !nextTo;
    const bothFilled = nextFrom && nextTo;
    const rangeOk = !bothFilled || nextFrom <= nextTo;
    if ((bothEmpty || bothFilled) && rangeOk) {
      // só emite se mudou de fato pra evitar refetch redundante
      if (nextFrom !== from || nextTo !== to) onChange({ from: nextFrom, to: nextTo });
    }
  }

  // Estado do calendário único
  const [month, setMonth] = React.useState<Date>(() =>
    parseIsoValue(from) ?? new Date(),
  );
  const [view, setView] = React.useState<View>('days');
  const [ygStart, setYgStart] = React.useState(() =>
    calcYgStart(getYear(parseIsoValue(from) ?? new Date())),
  );

  // Inputs de texto editáveis
  const [fromInput, setFromInput] = React.useState(() => fmtDisplay(from));
  const [toInput, setToInput] = React.useState(() => fmtDisplay(to));

  // Sincroniza inputs quando props mudam externamente (ex: limpar)
  React.useEffect(() => { setFromInput(fmtDisplay(from)); }, [from]);
  React.useEffect(() => { setToInput(fmtDisplay(to)); }, [to]);

  // Clique-fora e Escape ficam por conta do AnchoredPopover.

  // Ao abrir: reseta view e posiciona o calendário no mês do "from" atual
  React.useEffect(() => {
    if (!open) return;
    setView('days');
    const fd = parseIsoValue(from) ?? new Date();
    setMonth(fd);
    setYgStart(calcYgStart(getYear(fd)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Highlight do calendário usa internal pra refletir digitação imediata
  const fromDate = parseIsoValue(internalFrom);
  const toDate = parseIsoValue(internalTo);
  const rangeInvalid = !!(internalFrom && internalTo && internalFrom > internalTo);

  // ── Label do trigger ──────────────────────────────────────────────────────

  function buildLabel(): string {
    const fd = fmtDisplay(from);
    const td = fmtDisplay(to);
    if (!fd && !td) return placeholder;
    if (fd && td) return `${fd} → ${td}`;
    if (fd) return `Desde ${fd}`;
    return `Até ${td}`;
  }

  const triggerLabel = buildLabel();
  const hasValue = !!(from || to);

  // ── Seleção por calendário ─────────────────────────────────────────────────
  // Calendário único: 1º clique define "from". 2º clique define "to" (se a
  // data for >= from). Se já havia um range completo, ou o 2º clique cai
  // numa data anterior ao "from", (re)começa a seleção a partir do dia clicado.

  function handleDaySelect(iso: string) {
    const hasCompleteRange = !!internalFrom && !!internalTo;
    const isSecondClick = !!internalFrom && !hasCompleteRange;

    if (isSecondClick && iso >= internalFrom) {
      setInternalTo(iso);
      setToInput(fmtDisplay(iso));
      emitIfReady(internalFrom, iso);
      return;
    }

    setInternalFrom(iso);
    setInternalTo('');
    setFromInput(fmtDisplay(iso));
    setToInput('');
    emitIfReady(iso, '');
  }

  // ── Inputs editáveis ───────────────────────────────────────────────────────

  function handleFromInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const masked = applyDateMask(e.target.value);
    setFromInput(masked);
    if (masked.length === 10) {
      const iso = parseBrDate(masked);
      if (iso) {
        setInternalFrom(iso);
        const parsed = parseIsoValue(iso)!;
        setMonth(parsed);
        setYgStart(calcYgStart(getYear(parsed)));
        emitIfReady(iso, internalTo);
      }
    } else if (masked.length === 0) {
      setInternalFrom('');
      emitIfReady('', internalTo);
    }
  }

  function handleFromInputBlur() {
    if (fromInput.length > 0 && !parseBrDate(fromInput)) {
      setFromInput(fmtDisplay(internalFrom));
    }
  }

  function handleToInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const masked = applyDateMask(e.target.value);
    setToInput(masked);
    if (masked.length === 10) {
      const iso = parseBrDate(masked);
      if (iso) {
        setInternalTo(iso);
        const parsed = parseIsoValue(iso)!;
        setMonth(parsed);
        setYgStart(calcYgStart(getYear(parsed)));
        emitIfReady(internalFrom, iso);
      }
    } else if (masked.length === 0) {
      setInternalTo('');
      emitIfReady(internalFrom, '');
    }
  }

  function handleToInputBlur() {
    if (toInput.length > 0 && !parseBrDate(toInput)) {
      setToInput(fmtDisplay(internalTo));
    }
  }

  // ── Shortcuts ──────────────────────────────────────────────────────────────

  function applyToday() {
    const iso = toIso(new Date());
    setInternalFrom(iso); setInternalTo(iso);
    setFromInput(fmtDisplay(iso)); setToInput(fmtDisplay(iso));
    emitIfReady(iso, iso);
  }

  function applyLast7() {
    const toIso2 = toIso(new Date());
    const fromIso = toIso(addDays(new Date(), -6));
    setInternalFrom(fromIso); setInternalTo(toIso2);
    setFromInput(fmtDisplay(fromIso)); setToInput(fmtDisplay(toIso2));
    emitIfReady(fromIso, toIso2);
  }

  function applyThisMonth() {
    const now = new Date();
    const fromIso = toIso(dfStartOfMonth(now));
    const toIso2 = toIso(dfEndOfMonth(now));
    setInternalFrom(fromIso); setInternalTo(toIso2);
    setFromInput(fmtDisplay(fromIso)); setToInput(fmtDisplay(toIso2));
    emitIfReady(fromIso, toIso2);
  }

  function clearRange() {
    setInternalFrom(''); setInternalTo('');
    setFromInput(''); setToInput('');
    emitIfReady('', '');
  }

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className={cn('relative', className)}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={ariaLabel ?? 'Filtro de período'}
        className={cn(
          'flex h-9 w-full items-center gap-2 rounded-xl border bg-[var(--surface)] px-2.5 text-sm transition',
          'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]',
          open && 'ring-2 ring-[var(--accent)]',
          rangeInvalid
            ? 'border-red-500 text-red-500'
            : 'border-[var(--border)] text-[var(--fg)]',
        )}
      >
        <Calendar
          className={cn(
            'h-3.5 w-3.5 shrink-0',
            rangeInvalid ? 'text-red-400' : 'text-[var(--fg-muted)]',
          )}
        />
        <span
          className={cn(
            'flex-1 text-left truncate',
            !hasValue && 'text-[var(--fg-muted)]',
          )}
        >
          {triggerLabel}
        </span>
      </button>

      {/* Popover */}
      <AnchoredPopover
        anchorRef={triggerRef}
        open={open}
        onClose={() => setOpen(false)}
        width={320}
        align="end"
      >
        {/* Inputs de texto */}
        <div className="flex items-end gap-3 px-3 pt-3 pb-2 border-b border-[var(--popover-border,var(--card-border))]">
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-[10px] font-medium text-[var(--fg-muted)] uppercase tracking-wide">
              De
            </label>
            <input
              type="text"
              value={fromInput}
              onChange={handleFromInputChange}
              onBlur={handleFromInputBlur}
              placeholder="DD/MM/AAAA"
              maxLength={10}
              className={cn(
                'h-8 w-full rounded-lg border px-2.5 text-sm bg-transparent',
                'border-[var(--border)] text-[var(--fg)] placeholder:text-[var(--fg-muted)]',
                'focus:outline-none focus:ring-1 focus:ring-[var(--accent)]',
              )}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-[10px] font-medium text-[var(--fg-muted)] uppercase tracking-wide">
              Até
            </label>
            <input
              type="text"
              value={toInput}
              onChange={handleToInputChange}
              onBlur={handleToInputBlur}
              placeholder="DD/MM/AAAA"
              maxLength={10}
              className={cn(
                'h-8 w-full rounded-lg border px-2.5 text-sm bg-transparent',
                'border-[var(--border)] text-[var(--fg)] placeholder:text-[var(--fg-muted)]',
                'focus:outline-none focus:ring-1 focus:ring-[var(--accent)]',
              )}
            />
          </div>
        </div>

        {/* Calendário único */}
        <div>
          <MiniCalendar
            month={month}
            onMonthChange={setMonth}
            fromDate={fromDate}
            toDate={toDate}
            onSelect={handleDaySelect}
            view={view}
            onViewChange={setView}
            ygStart={ygStart}
            onYgStartChange={setYgStart}
          />
        </div>

        {/* Footer com shortcuts */}
        <div className="flex items-center justify-between border-t border-[var(--popover-border,var(--card-border))] px-3 py-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={applyToday}
              className="rounded-md px-2 py-1 text-[11px] text-[var(--fg-muted)] hover:text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] transition"
            >
              Hoje
            </button>
            <button
              type="button"
              onClick={applyLast7}
              className="rounded-md px-2 py-1 text-[11px] text-[var(--fg-muted)] hover:text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] transition"
            >
              Últimos 7 dias
            </button>
            <button
              type="button"
              onClick={applyThisMonth}
              className="rounded-md px-2 py-1 text-[11px] text-[var(--fg-muted)] hover:text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] transition"
            >
              Mês atual
            </button>
          </div>
          <button
            type="button"
            onClick={clearRange}
            className="text-[11px] text-[var(--fg-muted)] hover:text-[var(--fg)] transition"
          >
            Limpar
          </button>
        </div>
      </AnchoredPopover>
    </div>
  );
}
